import {
    Avatar,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {
    Cancel as CancelIcon,
    CheckCircle as CheckCircleIcon,
    Email as EmailIcon,
    Person as PersonIcon,
    PersonAdd as PersonAddIcon,
    SupervisorAccount as SupervisorAccountIcon,
    VpnKey as VpnKeyIcon,
    Schedule as ScheduleIcon,
    Event as EventIcon,
    EventNote as TemplateSetIcon,
} from '@mui/icons-material';
import { useI18nContext } from '@/i18n/i18n-react';

interface AddUserModalProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (user: {
        name: string;
        mail: string;
        auth: string;
        active: boolean;
        admin: boolean;
        scheduleEditor: boolean;
        scheduleTemplateEditor: boolean;
        eventEditor: boolean;
    }) => void;
    newUser: {
        name: string;
        mail: string;
        auth: string;
        active: boolean;
        admin: boolean;
        scheduleEditor: boolean;
        scheduleTemplateEditor: boolean;
        eventEditor: boolean;
    };
    onUserChange: (user: {
        name: string;
        mail: string;
        auth: string;
        active: boolean;
        admin: boolean;
        scheduleEditor: boolean;
        scheduleTemplateEditor: boolean;
        eventEditor: boolean;
    }) => void;
    authCodeEnabled?: boolean;
}

export const AddUserModal = ({
    open,
    onClose,
    onSubmit,
    newUser,
    onUserChange,
    authCodeEnabled = true,
}: AddUserModalProps) => {
    const { LL } = useI18nContext();

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Stack direction="row" sx={{ gap: 2, alignItems: 'center' }}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                        <PersonAddIcon />
                    </Avatar>
                    <Box>
                        <Typography variant="h5" fontWeight={700}>
                            {LL.addUserModal.title()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {LL.addUserModal.subtitle()}
                        </Typography>
                    </Box>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Stack sx={{ mt: 2, gap: 3 }}>
                    <TextField
                        label={LL.addUserModal.name()}
                        value={newUser.name}
                        onChange={(e) => onUserChange({ ...newUser, name: e.target.value })}
                        required
                        fullWidth
                        autoFocus
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                ),
                            },
                        }}
                    />
                    <TextField
                        label={LL.addUserModal.email()}
                        type="email"
                        value={newUser.mail}
                        onChange={(e) => onUserChange({ ...newUser, mail: e.target.value })}
                        fullWidth
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                ),
                            },
                        }}
                    />
                    {authCodeEnabled && (
                        <TextField
                            label={LL.addUserModal.authPin()}
                            value={newUser.auth}
                            onChange={(e) => onUserChange({ ...newUser, auth: e.target.value })}
                            required
                            fullWidth
                            helperText={LL.addUserModal.authPinHelperText()}
                            slotProps={{
                                htmlInput: { maxLength: 4 },
                                input: {
                                    startAdornment: (
                                        <VpnKeyIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                    ),
                                },
                            }}
                        />
                    )}
                    <Divider />

                    <Stack direction="row" sx={{ gap: 2, flexWrap: 'wrap' }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={newUser.active}
                                    onChange={(e) =>
                                        onUserChange({ ...newUser, active: e.target.checked })
                                    }
                                    icon={<CancelIcon />}
                                    checkedIcon={<CheckCircleIcon />}
                                />
                            }
                            label={
                                <Box>
                                    <Typography variant="body2" fontWeight={600}>
                                        {LL.userCard.activeUser()}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {LL.userCard.activeUserDescription()}
                                    </Typography>
                                </Box>
                            }
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={newUser.admin}
                                    onChange={(e) =>
                                        onUserChange({ ...newUser, admin: e.target.checked })
                                    }
                                    icon={<PersonIcon />}
                                    checkedIcon={<SupervisorAccountIcon />}
                                />
                            }
                            label={
                                <Box>
                                    <Typography variant="body2" fontWeight={600}>
                                        {LL.userCard.administrator()}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {LL.userCard.administratorDescription()}
                                    </Typography>
                                </Box>
                            }
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={newUser.scheduleEditor}
                                    onChange={(e) =>
                                        onUserChange({
                                            ...newUser,
                                            scheduleEditor: e.target.checked,
                                        })
                                    }
                                    icon={<ScheduleIcon />}
                                    checkedIcon={<ScheduleIcon />}
                                />
                            }
                            label={LL.userCard.scheduleEditor()}
                        />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={newUser.scheduleTemplateEditor}
                                    onChange={(e) =>
                                        onUserChange({
                                            ...newUser,
                                            scheduleTemplateEditor: e.target.checked,
                                        })
                                    }
                                    icon={<TemplateSetIcon />}
                                    checkedIcon={<TemplateSetIcon />}
                                />
                            }
                            label={LL.userCard.scheduleTemplateEditor()}
                        />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={newUser.eventEditor}
                                    onChange={(e) =>
                                        onUserChange({
                                            ...newUser,
                                            eventEditor: e.target.checked,
                                        })
                                    }
                                    icon={<EventIcon />}
                                    checkedIcon={<EventIcon />}
                                />
                            }
                            label={LL.userCard.eventEditor()}
                        />
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button onClick={onClose} variant="outlined">
                    {LL.common.cancel()}
                </Button>
                <Button
                    onClick={() => onSubmit(newUser)}
                    variant="contained"
                    disabled={!newUser.name || (authCodeEnabled && newUser.auth.length !== 4)}
                    startIcon={<PersonAddIcon />}
                >
                    {LL.addUserModal.createUser()}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
